import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { site } from '../data/site';
import { HeadContext } from './head';

type Props = {
  title: string;
  description: string;
  noindex?: boolean;
  schema?: object | object[];
  /** The page's short name in the breadcrumb, when the title carries search words. */
  breadcrumb?: string;
};
export function Seo({ title, description, noindex = false, schema, breadcrumb }: Props) {
  const { pathname } = useLocation();
  const canonical = new URL(pathname, site.origin).toString();
  const fullTitle = title === site.name ? title : `${title} | ${site.name}`;
  // During `renderToString` this is the one chance to say what the head should be.
  useContext(HeadContext)?.collect({ title: fullTitle, description, noindex, path: pathname });
  useEffect(() => {
    document.title = fullTitle;
    const set = (selector: string, attribute: string, value: string) => {
      let element = document.head.querySelector<HTMLElement>(selector);
      if (!element) {
        const [, attributeName, attributeValue] = selector.match(/\[(\w+)="([^"]+)"\]/) ?? [];
        element = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
        if (attributeName && attributeValue) element.setAttribute(attributeName, attributeValue);
        document.head.append(element);
      }
      element.setAttribute(attribute, value);
    };
    set('meta[name="description"]', 'content', description);
    set('link[rel="canonical"]', 'href', canonical);
    set('meta[property="og:title"]', 'content', fullTitle);
    set('meta[property="og:description"]', 'content', description);
    set('meta[property="og:url"]', 'content', canonical);
    set('meta[name="twitter:title"]', 'content', fullTitle);
    set('meta[name="twitter:description"]', 'content', description);
    const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (noindex) set('meta[name="robots"]', 'content', 'noindex,nofollow');
    else robots?.remove();
  }, [canonical, description, fullTitle, noindex]);

  // Rendered, not appended from the effect: an effect never runs during
  // `renderToString`, so an imperative script left the structured data out of
  // every prerendered page and only appeared once the bundle had executed.
  // Returned as markup, it is in the HTML that crawlers and link unfurlers read.
  // JSON-LD is valid anywhere in the document, so sitting in the body is fine,
  // and React keeps it in step on client-side navigation.
  //
  // `<` is escaped because a `</script>` inside the JSON would otherwise close
  // this tag early; the rest of the payload is our own data.
  const graph = [
    ...(schema ? (Array.isArray(schema) ? schema : [schema]) : [websiteSchema()]),
    // Every page below the home page is one step from it, and search results show that step.
    ...(pathname === '/' || noindex ? [] : [breadcrumbSchema(breadcrumb ?? title, canonical)]),
  ];
  const json = JSON.stringify(graph.length === 1 ? graph[0] : graph).replaceAll('<', '\\u003c');
  return (
    <script
      type="application/ld+json"
      data-route-schema
      dangerouslySetInnerHTML={{ __html: json }}
    />
  );
}

const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: site.name,
  url: site.origin,
});

const breadcrumbSchema = (title: string, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: site.origin },
    { '@type': 'ListItem', position: 2, name: title, item: url },
  ],
});
